import { type ReactNode } from 'react';

import { Box, Link, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import StatusBar from '@/designs-dev/device/StatusBar';
import Navigation from '@/karehero-app/shell/Navigation';
import Body from '@/karehero-app/shell/Body';
import ArticleCard from '@/karehero-app/home/ArticleCard';
import Card from '@/components/wrapped/Card';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
// Get rewarded card hidden for now — restore this import with its render below.
// import RewardCard from '@/karehero-app/home/RewardCard';

const noop = () => {};

// Frosted (translucent + blur) background so content shows through faintly behind
// the sticky footer. backdrop-filter has no system prop / token, so it needs sx.
const frostedFooter = (theme: any) => ({
  backgroundColor: alpha(theme.palette.background.default, 0.25),
  backdropFilter: 'blur(5px)',
  WebkitBackdropFilter: 'blur(5px)',
});

// Post-sign-in home. The entry cards are the consolidated Card, no squiggle.
// They were llama SquiggleCards, which meant every entry card carried a
// decorative flourish and a colour prop deciding its tint — and the tints never
// matched the Figma anyway, which asks for lavender and sunflower where
// SquiggleCard only ships cherry, lime and warmOrange. A plain card says the
// same thing without a colour decision per card.
const HomeScreen = ({
  onNotifications,
  onFinancialStatus,
  title,
  onCard,
  onSwitch,
  lead,
  footer = (
    <Button
      variant='primary'
      size='lg'
      isFullWidth
      ariaLabel='Book a call'
      onPress={noop}
    >
      Book a call
    </Button>
  ),
  cards,
}: {
  /** Bell tap — opens the notification centre. Omitted in stories/handover. */
  onNotifications?: () => void;
  /** Financial status card tap — opens the financial assessment flow. */
  onFinancialStatus?: () => void;
  /**
   * The person being cared for, beside the logo.
   *
   * Optional so the master's own bar is unchanged. The architecture cut passes
   * it on every tab: the app is about one person, and a bar that names them on
   * the profile and nowhere else makes the name read as that page's heading
   * rather than as whose app this is.
   */
  title?: string;
  /**
   * Any card tapped, by its title.
   *
   * Optional, so the master's cards stay inert. The architecture cut uses it to
   * open the card's explanation as a sheet, where production pushes a page.
   */
  onCard?: (title: string) => void;
  /** Someone else picked in the bar's switcher. See Navigation's `onSwitch`. */
  onSwitch?: (name: string) => void;
  /**
   * A card above the entry cards: the one thing to do next, like the benefits
   * calculator 1.0 draft's Check your eligibility. Omit for Home as it ships.
   */
  lead?: ReactNode;
  /**
   * What floats over the tab bar, in place of Book a call: the calculator 1.0
   * draft's Tell us what you need. Null for nothing there.
   */
  footer?: ReactNode;
  /**
   * In place of the entry cards and the articles, null for none. Omit for Home
   * as it ships.
   */
  cards?: ReactNode;
} = {}) => (
  <ScreenRoot relative>
    <StatusBar />

    <Navigation
      title={title}
      onNotifications={onNotifications}
      avatarInitials='OP'
      onSwitch={onSwitch}
    />

    <Body pb={footer ? '96px' : undefined}>
      <Stack spacing={2}>
        {lead}

        {cards !== undefined ? (
          cards
        ) : (
          <>
            {/* two entry cards */}
            <Stack sx={{ alignItems: 'stretch' }} direction='row' spacing={2}>
              <Card
                titleVariant='h4'
                title='Financial status'
                subtitle='Understand your finances'
                onClick={
                  onFinancialStatus ?? (() => onCard?.('Financial status'))
                }
                sx={{ flex: 1 }}
              />
              <Card
                titleVariant='h4'
                title='Care assessment'
                subtitle='Assess your care needs'
                onClick={() => onCard?.('Care assessment')}
                sx={{ flex: 1 }}
              />
            </Stack>

            <Card
              titleVariant='h4'
              title='Power of attorney'
              subtitle='Get covered'
              onClick={() => onCard?.('Power of attorney')}
            />

            {/* Named for the thing it opens, as the choose-next option and the bar
            inside the flow are. No progress badge here: Home is where you find
            it, not where you are told how far in you already are. */}
            <Card
              titleVariant='h4'
              title='Benefits calculator'
              subtitle='Carers miss out on up to £6,000 a year in unclaimed benefits'
              onClick={() => onCard?.('Benefits calculator')}
            />

            {/* suggested articles */}
            <Stack
              sx={{
                alignItems: 'baseline',
                justifyContent: 'space-between',
                pt: 1,
              }}
              direction='row'
            >
              <Typography variant='h3'>Suggested articles</Typography>
              <Link component='button' type='button' variant='body2'>
                See all
              </Link>
            </Stack>

            <ArticleCard
              title='Understanding care funding options'
              summary='A plain-English guide to who pays for care and how.'
              onClick={noop}
            />
          </>
        )}
      </Stack>
    </Body>

    {/* sticky Book a call, floating above the tab bar */}
    {footer && (
      <Box
        sx={[
          {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            px: 3,
            py: 1.5,
            borderTop: 1,
            borderColor: 'custom.neutral.200',
          },
          frostedFooter,
        ]}
      >
        {footer}
      </Box>
    )}
  </ScreenRoot>
);

export default HomeScreen;
